/** What this workspace *uses*, counted — **gate 15, decision P-06: measure, do not enforce**.
 *
 * Members by role, and how many companies and sites there are. That is the whole of it.
 *
 * **There is no price in this product, no limit and no enforcement, and this file is where that is
 * made structural rather than promised.** A tally is a plain object whose key set a test pins
 * (`MEMBER_TALLY_KEYS`, `ESTATE_TALLY_KEYS`), and so is this module's export list
 * (`licences.test.ts`). A price field, a `limit` or a `seatsRemaining` added "just for display" is a
 * new key, and the test that fails is the one named after it. The pricing is an agreed
 * placeholder, owned by the founder, and not an engineer's to type into a component.
 *
 * **Counted from reads that succeeded, and from nothing else.** These functions take the rows a
 * successful request returned. A failed request has no rows, and the panel that calls them never
 * reaches them: `[]` and *"could not be read"* are different inputs and the panel renders them as
 * different things. A count of `0` is only ever a measurement.
 *
 * **Reuse, not a second read.** There is no `/licences` route and none is wanted: `useMembers`
 * (Admin-only on the server), `useEntities` and the sites each entity carries already answer the
 * question, and a second endpoint stating a count would be a second home for a fact the first
 * already holds.
 */

import type { Entity, Member } from "./queries";

/** The roles in the order a reader expects them: most to least privileged. */
export const ROLE_ORDER = ["admin", "approver", "contributor", "auditor", "viewer"] as const;

export interface MemberTally {
  /** People who can sign in today, by role. Every known role is present, `0` included — a measured zero. A role
   * the server returns that this list does not know gets its own key rather than being dropped, so
   * the rows always sum to `active`. */
  byRole: Record<string, number>;
  /** Everyone who can sign in. The sum of `byRole`. */
  active: number;
  /** Suspended, erased or not yet started: on the register, not counted above. */
  notCounted: number;
  /** Active on the register but past the date their access ended — an auditor's engagement is for
   * a period, and `identity.service.in_force` refuses a sign-in after `valid_to`. Not a seat in
   * use, for the same reason a suspended person is not, and listed apart so it is not hidden. */
  ended: number;
}

export interface EstateTally {
  entities: number;
  /** Every site on record. Retired sites are included — the sites route does not say which are
   * retired, and a count that quietly dropped them would be a claim this file cannot back. */
  sites: number;
}

/** The exact key sets, exported so a test can pin them. */
export const MEMBER_TALLY_KEYS = ["active", "byRole", "ended", "notCounted"] as const;
export const ESTATE_TALLY_KEYS = ["entities", "sites"] as const;

/** Today's date as the server reads it: UTC, `YYYY-MM-DD` (`datetime.now(UTC).date()`). */
export function todayUtc(now: Date = new Date()): string {
  return now.toISOString().slice(0, 10);
}

/** `today` is required, not defaulted: a count that silently used "whenever the caller forgot" is
 * a count nobody can reproduce. ISO dates compare correctly as strings.
 *
 * **The `valid_from` intersection that stood here is gone** (2026-10-23). It read
 * `& { valid_from?: string | null }` because `schema.d.ts` predated the server's projection of
 * that column, and it was optional on purpose so that a response omitting it read as "no start
 * date", which is what an older server meant. `make api-types` has now generated
 * `valid_from?: string | null` onto `MemberOut` itself, so the intersection collapsed to the
 * generated type and the hand-written half is removed rather than left to agree with it by
 * coincidence — which is the second home that eventually disagrees.
 */
export function tallyMembers(
  members: readonly Pick<Member, "role" | "status" | "valid_to" | "valid_from">[],
  today: string,
): MemberTally {
  const byRole: Record<string, number> = Object.fromEntries(ROLE_ORDER.map((role) => [role, 0]));
  let active = 0;
  let notCounted = 0;
  let ended = 0;
  for (const member of members) {
    if (member.status !== "active" || (member.valid_from != null && member.valid_from > today)) {
      notCounted += 1;
    } else if (member.valid_to != null && member.valid_to < today) {
      ended += 1;
    } else {
      byRole[member.role] = (byRole[member.role] ?? 0) + 1;
      active += 1;
    }
  }
  return { byRole, active, notCounted, ended };
}

/** One company and how many sites it carries. */
export interface CompanySites {
  id: string;
  name: string;
  jurisdiction: string;
  sites: number;
}

/** Sites by company — the same numbers `tallyEstate` sums, kept per row, so the rows can be shown
 * to add up to the total. Order is the server's (by legal name). */
export function sitesByCompany(
  entities: readonly Pick<Entity, "id" | "legal_name" | "jurisdiction" | "facility_count">[],
): CompanySites[] {
  return entities.map((entity) => ({
    id: entity.id,
    name: entity.legal_name,
    jurisdiction: entity.jurisdiction,
    sites: entity.facility_count,
  }));
}

/** **What a licence would be counted on** — the whole list, stated so the Administration screen
 * does not have to imply it. A closed set: a key added here is a new thing being measured, which
 * is a founder decision (P-06), and `licences.test.ts` pins it.
 *
 * Deliberately absent: any unit of *consumption* beyond these (documents, records, calculations,
 * reports, storage). They are not what P-06 measures, and a number that looks like usage would
 * invite a limit being built on it. */
export const LICENCE_MEASURES = [
  {
    key: "people",
    label: "People, by role",
    detail:
      "Everyone who can sign in to this workspace, counted once, in the role they hold here. " +
      "Suspended people, erased people and people whose access has not started or has ended are shown apart and are not counted.",
  },
  {
    key: "companies",
    label: "Companies",
    detail:
      "Every legal entity in the group. Whether a company is one that files is a separate fact " +
      "this count does not read.",
  },
  {
    key: "sites",
    label: "Sites, by company",
    detail: "Every site on record under each company. Retired sites are included.",
  },
] as const;

/** A reader would otherwise assume these counts are a record over time. Printed as a decision, not
 * left as blank space. */
export const LICENCE_AS_OF_NOW =
  "These are counts as of now. The product keeps no history of them, so a past month cannot be read back from here.";

export function tallyEstate(entities: readonly Pick<Entity, "facility_count">[]): EstateTally {
  return {
    entities: entities.length,
    sites: entities.reduce((sum, entity) => sum + entity.facility_count, 0),
  };
}
